import type { DeviceMapPoint, SiteDeviceItem, SiteDeviceType } from "@safers/api";
import { SEVERITY_PALETTE } from "@safers/ui/styles/severity";
import { severityColor } from "./severity";

export type ModelDeviceType = SiteDeviceType | "CCTV";
/** 지도 좌표(DeviceMapPoint)에 장비 목록(SiteDeviceItem)의 통신 상태·측정값을 더한 핀 데이터. */
export interface ModelDevicePoint extends Omit<DeviceMapPoint, "type"> {
  type: ModelDeviceType;
  statusLabel?: string;
  videoPath?: string;
  color?: string;
  selected?: boolean;
  measurements?: SiteDeviceItem["measurements"];
  lastSeenTime?: string;
  lastSeenAgo?: string;
  /** 지면에서 잰 설치 높이(m). 3D 에서 핀을 그 높이에 띄우고 지면까지 점선을 내린다. */
  altitude?: number | null;
}

export const OFFLINE_COLOR = SEVERITY_PALETTE.UNCERTAIN.fill;
const OFFLINE_FILL = SEVERITY_PALETTE.UNCERTAIN.mutedFill;
export const DELAYED_OPACITY = 0.5;
export const markerColor = (device: ModelDevicePoint) =>
  device.status === "OFFLINE" ? OFFLINE_COLOR : (device.color ?? severityColor(device.severity));
export const markerOpacity = (device: ModelDevicePoint) =>
  device.status === "DELAYED" ? DELAYED_OPACITY : 1;
/**
 * 핀 원의 채움·테두리·아이콘 색. 끊김은 연회색 바탕에 진회색 테두리·아이콘으로 뒤집는다 —
 * 진회색으로 채우는 위험도 '불확실'과 핀만 보고도 구분되게.
 */
export const markerPaint = (device: ModelDevicePoint) =>
  device.status === "OFFLINE"
    ? { fill: OFFLINE_FILL, ring: OFFLINE_COLOR, icon: OFFLINE_COLOR }
    : { fill: markerColor(device), ring: "white", icon: "white" };

export const MARKER_SLIDE_MS = 1000;
/** 끝에서 천천히 멈추는 이동 곡선(ease-out). t 는 0~1. */
export const slideEase = (t: number) => t * (2 - t);

export const isUnavailableCctv = (device: ModelDevicePoint) =>
  device.type === "CCTV" && (device.status === "OFFLINE" || !device.videoPath);
export const CCTV_UNAVAILABLE_LABEL = "오프라인 · 영상 없음";

export const DEVICE_TYPES: Record<ModelDeviceType, { label: string; path: string; color: string }> =
  {
    CCTV: { label: "CCTV", path: "M3 6h12v12H3ZM15 10l6-3v10l-6-3Z", color: "#2563eb" },
    HEAT_INDEX: {
      label: "체감온도계",
      color: "#c2410c",
      path: "M9 14V5a3 3 0 0 1 6 0v9a5 5 0 1 1-6 0Zm3-7v10m0 0a1 1 0 1 0 0 2 1 1 0 0 0 0-2Z",
    },
    GAS_DETECTOR: {
      label: "유해가스",
      color: "#047857",
      path: "M7 17H6a4 4 0 0 1-.6-7.95A6 6 0 0 1 17 7a5 5 0 0 1 1 9.9M8 20l2-6m3 6 2-6m3 6 2-6",
    },
    SMART_BAND: {
      label: "스마트밴드",
      color: "#7c3aed",
      path: "M8 7V2h8v5M8 17v5h8v-5M7 7h10v10H7ZM9 12h2l1-2 1 4 1-2h1",
    },
  };

export type ModelDeviceFilter = Record<ModelDeviceType, boolean>;

export const DEFAULT_DEVICE_FILTER: ModelDeviceFilter = {
  CCTV: true,
  HEAT_INDEX: true,
  GAS_DETECTOR: true,
  SMART_BAND: true,
};

export const filterDevices = (devices: ModelDevicePoint[], filter: ModelDeviceFilter) =>
  devices.filter((device) => filter[device.type]);

export const DEVICE_FOCUS_MS = 600;
/** 목록에서 장비를 고르면 3D 에서 이 거리(m)까지 다가간다. 이미 더 가까우면 그대로. */
export const DEVICE_FOCUS_DISTANCE = 45;
